/**
 * PatientJourneyTab (v36 — May 20, 2026)
 *
 * Renders the unified `/api/patients/{id}/journey` aggregation as a vertical timeline.
 * One row per event with icon, title, subtitle, type chip, and date. Includes a
 * type-filter chip bar at the top.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Calendar, Pill, TestTube, Bed, LogOut, FileText, Activity, Receipt, Stethoscope, Loader2, Filter,
} from 'lucide-react';
import { cn } from '../lib/utils';

const ICON_MAP = {
  appointment: Calendar,
  diagnosis: Stethoscope,
  prescription: Pill,
  pharmacy_dispense: Pill,
  lab_order: TestTube,
  lab_report: TestTube,
  admission: Bed,
  discharge: LogOut,
  discharge_summary: FileText,
  vitals: Activity,
  payment: Receipt,
};

const COLOR_MAP = {
  appointment: 'text-blue-600 bg-blue-50 border-blue-200',
  diagnosis: 'text-purple-600 bg-purple-50 border-purple-200',
  prescription: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  pharmacy_dispense: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  lab_order: 'text-orange-600 bg-orange-50 border-orange-200',
  lab_report: 'text-amber-600 bg-amber-50 border-amber-200',
  admission: 'text-red-600 bg-red-50 border-red-200',
  discharge: 'text-slate-700 bg-slate-100 border-slate-300',
  discharge_summary: 'text-slate-700 bg-slate-100 border-slate-300',
  vitals: 'text-pink-600 bg-pink-50 border-pink-200',
  payment: 'text-teal-600 bg-teal-50 border-teal-200',
};

const LABEL_MAP = {
  appointment: 'Appointment',
  diagnosis: 'Diagnosis',
  prescription: 'Prescription',
  pharmacy_dispense: 'Dispensed',
  lab_order: 'Lab Order',
  lab_report: 'Lab Report',
  admission: 'Admission',
  discharge: 'Discharge',
  discharge_summary: 'Discharge Summary',
  vitals: 'Vitals',
  payment: 'Payment',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return iso; }
};

export const PatientJourneyTab = ({ patientId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState(new Set());

  useEffect(() => {
    if (!patientId) return;
    let mounted = true;
    setLoading(true);
    api.get(`/patients/${patientId}/journey`, { params: { limit: 300 } })
      .then(res => { if (mounted) setData(res.data); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load patient journey'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [patientId]);

  const filteredTimeline = useMemo(() => {
    if (!data?.timeline) return [];
    if (activeFilters.size === 0) return data.timeline;
    return data.timeline.filter(e => activeFilters.has(e.type));
  }, [data, activeFilters]);

  const toggleFilter = (type) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };
  const clearFilters = () => setActiveFilters(new Set());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2" data-testid="journey-loading">
        <Loader2 className="animate-spin" size={20} /> Loading patient journey…
      </div>
    );
  }
  if (!data) return null;

  const totals = data.summary?.totals_by_type || {};
  const visibleTypes = Object.keys(totals);

  return (
    <div className="space-y-5" data-testid="patient-journey-tab">
      {/* Summary strip */}
      <div className="bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Patient Journey</p>
            <p className="text-base font-bold text-slate-900 mt-0.5">
              {data.summary?.total_events ?? 0} events
              <span className="text-xs font-normal text-slate-500 ml-2">
                {data.summary?.first_event && `from ${(data.summary.first_event || '').slice(0, 10)}`}
                {data.summary?.last_event && ` to ${(data.summary.last_event || '').slice(0, 10)}`}
              </span>
            </p>
          </div>
          {activeFilters.size > 0 && (
            <button
              onClick={clearFilters}
              className="text-xs text-slate-500 hover:text-slate-700 underline self-start md:self-auto"
              data-testid="journey-clear-filters">
              Clear filters ({activeFilters.size})
            </button>
          )}
        </div>
      </div>

      {/* Type filter chips */}
      {visibleTypes.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-slate-400" />
          {visibleTypes.map(t => {
            const isActive = activeFilters.has(t);
            const Icon = ICON_MAP[t] || Activity;
            return (
              <button
                key={t}
                onClick={() => toggleFilter(t)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all',
                  isActive
                    ? COLOR_MAP[t] || 'text-slate-600 bg-slate-100 border-slate-300'
                    : 'text-slate-500 bg-white border-slate-200 hover:bg-slate-50'
                )}
                data-testid={`journey-filter-${t}`}>
                <Icon size={11} />
                {LABEL_MAP[t] || t}
                <span className="opacity-70">· {totals[t]}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Timeline */}
      {filteredTimeline.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400" data-testid="journey-empty">
          {activeFilters.size > 0
            ? 'No events match the selected filters.'
            : 'No history recorded yet for this patient.'}
        </div>
      ) : (
        <div className="relative pl-6" data-testid="journey-timeline">
          {/* Vertical line */}
          <div className="absolute left-2.5 top-2 bottom-2 w-0.5 bg-slate-200" aria-hidden="true" />

          <div className="space-y-3">
            {filteredTimeline.map((e, idx) => {
              const Icon = ICON_MAP[e.type] || Activity;
              const color = COLOR_MAP[e.type] || 'text-slate-600 bg-slate-50 border-slate-200';
              return (
                <div key={`${e.type}-${e.ref_id}-${idx}`} className="relative" data-testid={`journey-event-${idx}`}>
                  {/* Dot */}
                  <div className={cn(
                    'absolute -left-3.5 top-3 h-5 w-5 rounded-full border-2 flex items-center justify-center bg-white',
                    color.split(' ').find(c => c.startsWith('border-')) || 'border-slate-200'
                  )}>
                    <Icon size={10} className={color.split(' ').find(c => c.startsWith('text-')) || 'text-slate-600'} />
                  </div>

                  {/* Card */}
                  <div className="bg-white border border-slate-200 rounded-lg p-3 hover:border-slate-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={cn('text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border', color)}>
                            {LABEL_MAP[e.type] || e.type}
                          </span>
                          <span className="text-[10px] text-slate-400">{fmtDate(e.date)}</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 truncate">{e.title}</p>
                        {e.subtitle && (
                          <p className="text-xs text-slate-500 mt-0.5">{e.subtitle}</p>
                        )}
                        {/* Inline data preview for select types */}
                        {e.type === 'prescription' && e.data?.medications?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {e.data.medications.slice(0, 4).map((m, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {m.name || m.drug_name || '?'}
                                {m.dosage && ` · ${m.dosage}`}
                              </span>
                            ))}
                          </div>
                        )}
                        {e.type === 'vitals' && (
                          <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-slate-600">
                            {e.data?.blood_pressure_systolic && <span><b>BP</b> {e.data.blood_pressure_systolic}/{e.data.blood_pressure_diastolic}</span>}
                            {e.data?.pulse_rate && <span><b>HR</b> {e.data.pulse_rate}</span>}
                            {e.data?.temperature && <span><b>T</b> {e.data.temperature}°C</span>}
                            {e.data?.oxygen_saturation && <span><b>SpO₂</b> {e.data.oxygen_saturation}%</span>}
                            {e.data?.blood_sugar && <span><b>Sugar</b> {e.data.blood_sugar}</span>}
                          </div>
                        )}
                        {e.type === 'payment' && e.data?.receipt_number && (
                          <p className="text-[10px] text-slate-500 mt-1 font-mono">{e.data.receipt_number}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientJourneyTab;
